<template>
<div class="qr-code-container">
  <div class="code-header">
    <div class="code-title">{{ title }}</div>
    <div class="code-sub-title-container">
      <div v-for="subTitle of subTitles" :key="subTitle.title" class="code-sub-title">
        <div>{{ subTitle.title}} : </div>
        <div>{{ subTitle.value }}</div>
      </div>
    </div>
  </div>
  <div ref="qrContainer" >
    <!-- QR 코드 만료 시 -->
    <div v-if="!remainSeconds" class="qr-expired">
      <!-- QR 다시 만들기 버튼 -->
      <div class="expired-text">{{ $t('QR 다시 만들기') }}</div>
      <vs-button class="retry-button" color="primary" type="border" icon-pack="feather" icon="icon-refresh-cw" size="large" @click="createQrCode"/>
    </div>

    <!-- QR 코드 유효 시 -->
    <div v-else class="qr-code-wrapper">
      <div>
        <vue-q-r-code-component :text="code" :size="qrCodeSize" class="qr-code" />
      </div>
    </div>
  </div>
  <div>
    <div>
      <p v-if="remainSeconds" class="flex items-center justify-center" style="margin-bottom: 14px;">
        <feather-icon class="mr-2" icon="AlertCircleIcon"/>
        <span class="mr-4">{{ $t('남은시간') }}</span>
        <span style="color:#E42728; font-weight: bold">{{remainSeconds}}초</span>
      </p>
      <p v-else class="flex items-center justify-center" style="margin-bottom: 14px;">
        <feather-icon style="color:#E42728;" class="mr-2" icon="AlertTriangleIcon"/>
        <span style="color:#E42728; font-weight: bold">{{ $t('QR 만료됨') }}</span>
      </p>
    </div>
    <div style="margin-bottom: 16px;">
      <vs-button class="w-full" color="primary" @click="onClick">{{ $t('닫기') }}</vs-button>
    </div>
  </div>
</div>
</template>

<script>
import VueQRCodeComponent from "vue-qrcode-component";
import HttpRequest from "@/util/HttpRequest";

export default {
  name: 'QrAccessCode',
  components: {
    VueQRCodeComponent
  },
  emits: ['close'],
  props: {
    value: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      isOpen: false,
      작업허가명: '',
      허가번호: '',
      작업시간: '',
      사업장명: '',
      업체명: '',
      구분: '',
      작업장소: '',
      작업내용: '',
      regionId: 0,
      zoneId: 0,
      locationId: 0,
      // QR 코드를 전달받은 시각
      qrCreatedAt: 0,
      // QR 코드
      code: '',
      qrCodeSize: 256,

      remainSeconds: 0,
      // 타이머 인터벌
      timerInterval: null,

      // QR 코드 갱신 주기 (초)
      qrRefreshSec: 30,
    }
  },
  computed: {
    projectId() {
      return this.$route.params.proj_id;
    },
    title() {
      const titleList = [];
      if (this.작업허가명) {
        titleList.push(this.작업허가명);
      }
      if (this.허가번호) {
        titleList.push(this.허가번호);
      }
      return titleList.join(', ');
    },
    subTitles() {
      const subTitleList = [];
      if (this.사업장명) {
        subTitleList.push({ title: '사업장명', value: this.사업장명 });
      }
      if (this.업체명) {
        subTitleList.push({ title: '업체(부서)명', value: this.업체명 });
      }
      if (this.구분) {
        subTitleList.push({ title: '구분', value: this.구분 });
      }
      if (this.작업장소) {
        subTitleList.push({ title: '작업장소', value: this.작업장소 });
      }
      if (this.작업내용) {
        subTitleList.push({ title: '작업내용', value: this.작업내용 });
      }
      return subTitleList;
    },
    qrContainerHeight() {
      return this.$refs.qrContainer ? this.$refs.qrContainer.clientHeight : 0;
    }
  },
  watch: {
    qrContainerHeight(newHeight) {
      if (newHeight > 0) {
        this.qrCodeSize = Math.floor(newHeight * 0.8);
      }
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.updateQrCodeSize();
    })
    window.addEventListener('resize', this.updateQrCodeSize);

    this.getWorkPermitName();
    this.getPermitNumber();
    this.getWorkTime();
    this.getFieldName();
    this.getCompanyName();
    this.getWorkType();
    this.getZoneName();
    this.getWorkContent();
    this.getRegionId();
    this.getZoneId();

    this.createQrCode();

    this.timerInterval = setInterval(() => {
      this.updateRemainSeconds();
    }, 100);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.updateQrCodeSize);
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  },
  methods: {
    onClick() {
      this.$emit('close');
    },

    createQrCode() {
      const projectId = this.value.project_id;
      const params = {
        type: 'access',
      };
      if (this.value.board_id) {
        params.bid = this.value.board_id;
      }
      if (this.value.id) {
        params.pid = this.value.id;
      }
      if (this.zoneId) {
        params.zid = this.zoneId;
      }
      if (this.regionId) {
        params.rid = this.regionId;
      }

      if (projectId) {
        const api = `/api/v1/project/qr/${projectId}/code`;

        HttpRequest.get(api, { params }).then(response => {
          const {data} = response;
          this.qrCreatedAt = new Date().getTime();
          this.remainSeconds = this.qrRefreshSec;
          this.code = data;
        }).catch(err => {
          console.error(err);
          const customAlert = new CustomEvent("customAlert", {
            detail: {
              title: "QR 코드 생성 실패",
              message: "QR 코드 생성에 실패했습니다. 잠시 후 다시 시도해주세요.",
            },
          });
          window.dispatchEvent(customAlert);
          this.onClick();
        })
      } else {
        const customAlert = new CustomEvent("customAlert", {
          detail: {
            title: "QR 코드 생성 실패",
            message: "프로젝트 정보가 없습니다.",
          },
        });
        window.dispatchEvent(customAlert);

        this.onClick();
      }
    },

    // 작업허가명
    getWorkPermitName() {
      const contents = this.value.post_contents;
      for (const content of contents) {
        if (content.data_key === 'body.작업정보.작업허가명') {
          this.작업허가명 = content.data_value;
          return;
        }
      }
      this.작업허가명 = this.value.board.board_title || this.value.post_title;
    },

    // 허가번호
    getPermitNumber() {
      const contents = this.value.post_contents;
      for (const content of contents) {
        if (content.data_key === 'body.작업정보.허가번호') {
          this.허가번호 = content.data_value;
          return;
        }
      }
      this.허가번호 = '';
    },

    // 작업시간
    getWorkTime() {
      const contents = this.value.post_contents;
      let date = '';
      let startTime = '';
      let endTime = '';
      for (const content of contents) {
        if (content.data_key === 'body.작업허가.작업시간.시작') {
          startTime = content.data_value || '';
        } else if (content.data_key === 'body.작업허가.작업시간.종료') {
          endTime = content.data_value || '';
        } else if (content.data_key === 'body.작업허가.작업시작일시') {
          date = content.data_value || '';
        }
      }

      const temp1 = [];
      if (date) temp1.push(date);
      if (startTime) temp1.push(startTime);
      const start = temp1.join(' ');

      const temp2 = [];
      if (start) temp2.push(start);
      if (endTime) temp2.push(endTime);
      this.작업시간 = temp2.join(' ~ ');
    },

    // 사업장명
    getFieldName() {
      const contents = this.value.post_contents;
      for (const content of contents) {
        if (content.data_key === 'field.field_name') {
          this.사업장명 = content.data_value || '';
          return;
        }
      }
      if (this.value.field) {
        this.사업장명 = this.value.field.field_name || '';
      } else {
        this.사업장명 = '';
      }
    },

    // 업체명
    getCompanyName() {
      const contents = this.value.post_contents;
      for (const content of contents) {
        if (content.data_key === 'body.작업정보.업체명.com_name') {
          this.업체명 = content.data_value;
          return;
        }
      }
      this.업체명 = '';
    },

    //구분
    getWorkType() {
      const contents = this.value.post_contents;
      for (const content of contents) {
        if (content.data_key === 'body.작업구분') {
          this.구분 = content.data_value;
          return;
        }
      }
      this.구분 = '';
    },

    // 작업장소
    getZoneName() {
      const contents = this.value.post_contents;
      for (const content of contents) {
        if (content.data_key === 'body._etc.작업장소.zone.name') {
          this.작업장소 = content.data_value;
          return;
        }
      }
      this.작업장소 = '';
    },
    getWorkContent() {
      const contents = this.value.post_contents;
      for (const content of contents) {
        if (content.data_key === 'body.작업정보.작업내용') {
          this.작업내용 = content.data_value;
          return;
        }
      }
      this.작업내용 = '';
    },
    getRegionId() {
      const contents = this.value.post_contents;
      for (const content of contents) {
        if (content.data_key === 'body._etc.작업장소.region.id') {
          this.regionId = Number(content.data_value);
          return;
        }
      }
      this.regionId = 0;
    },
    getZoneId() {
      const contents = this.value.post_contents;
      for (const content of contents) {
        if (content.data_key === 'body._etc.작업장소.zone.id') {
          this.zoneId = Number(content.data_value);
          return;
        }
      }
      this.zoneId = 0;
    },

    updateQrCodeSize() {
      const container = this.$refs.qrContainer;

      if (container) {
        const width = container.clientWidth; // 컨테이너 폭
        const height = container.clientHeight; // 컨테이너 높이

        // 폭과 높이의 90% 계산
        const maxWidth = Math.floor(width * 0.9);
        const maxHeight = Math.floor(height * 0.9);

        // 더 작은 값 선택
        const qrSize = Math.min(maxWidth, maxHeight);

        // 크기 설정
        this.qrCodeSize = qrSize;

        // CSS 변수에 동적 크기 반영
        container.style.setProperty('--qr-size', `${qrSize}px`);
      }
    },


    updateRemainSeconds() {
      const now = new Date().getTime();
      let diff = now - this.qrCreatedAt;
      if (diff > this.qrRefreshSec * 1000) {
        diff = this.qrRefreshSec * 1000;
      }
      this.remainSeconds = this.qrRefreshSec - Math.floor(diff / 1000);
    }
  }
}
</script>

<style scoped lang="scss">
.qr-code-container {
  display: grid;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  grid-template-columns: 1fr;
  grid-template-rows: auto 1fr auto;

  .code-header {
    display: flex;
    flex-direction: column;

    .code-title {
      text-align: center;
      font-size: 20px;
      font-weight: 700;
      margin-top: 16px;
      margin-bottom: 12px;
    }
    .code-sub-title-container {
      padding: 0 30px;
      font-size: 15px;
      color: #3c3c3c;

      .code-sub-title {
        display: flex;
        flex-direction: row;
        margin-bottom: 8px;
        div {
          &:first-child {
            font-weight: 700;
            margin-right: 4px;
          }
        }
      }
    }
  }

  .qr-code-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;

    & > div {
      background-color: #fff !important;
      -webkit-forced-color-adjust: none !important;
      forced-color-adjust: none !important;
      color-scheme: light !important;
      -webkit-filter: none !important;
      filter: none !important;

      padding: 10px;
    }
  }

  .qr-code {
    width: var(--qr-size, 256px); // 58%; /* 컨테이너 높이의 60% */
    height: var(--qr-size, 256px); /* 컨테이너 높이의 60% */
    max-width: 100%; /* 최대 크기 제한 */
    max-height: 100%; /* 최대 크기 제한 */
    background-color: white;


    &::v-deep img {
      width: 100%;
      height: var(--qr-size, 256px);
      padding: 10px;
      border: 1px solid #ccc;
    }
  }

  .qr-expired {
    display: flex;
    height: 100%;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
  }

  .expired-icon {
    color: #ff4d4f;
  }

  .expired-text {
    font-size: 14px;
    color: #3c3c3c;
  }

  .retry-button {
    margin-top: 10px;
    padding: 60px 64px;
    font-size: 14px;
    font-weight: 500;
    border-radius: 6px;
    transition: all 0.2s;
  }
}
</style>
